"use client";
import { useState, useEffect, useRef } from 'react';
import { X, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';

interface UrgencyBannerProps {
  isVisible: boolean;
  onDismiss: () => void;
}

const urgencyMessages = [
  {
    icon: Zap,
    text: 'Limited Offer: Get 14 days free when you start your trial this week',
    mobileText: '14 days free - Start trial this week',
    cta: 'Claim Offer',
    mobileCta: 'Claim',
    link: '/pricing'
  },
  {
    icon: Zap,
    text: 'Start your free trial today - No credit card required',
    mobileText: 'Free trial - No credit needed',
    cta: 'Start Free Trial',
    mobileCta: 'Start Free Trial',
    link: '/pricing'
  },
];

export function UrgencyBanner({ isVisible, onDismiss }: UrgencyBannerProps) {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [animating, setAnimating] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // PERF: the rotation is paused while the tab is hidden. This banner is mounted by SiteHeader on
  // every marketing page, so an unconditional interval means a timer (and a header re-render every
  // 8s) running forever in every background tab.
  useEffect(() => {
    let messageInterval: ReturnType<typeof setInterval> | undefined;

    const start = () => {
      if (messageInterval) return;
      messageInterval = setInterval(() => {
        setAnimating(true);
        timeoutRef.current = setTimeout(() => {
          setCurrentMessageIndex((prev) => (prev + 1) % urgencyMessages.length);
          setAnimating(false);
        }, 300);
      }, 8000);
    };

    const stop = () => {
      if (messageInterval) clearInterval(messageInterval);
      messageInterval = undefined;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };

    const onVisibility = () => (document.visibilityState === 'visible' ? start() : stop());

    onVisibility();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
    };
  }, []);

  if (!isVisible) return null;

  const currentMessage = urgencyMessages[currentMessageIndex];
  const Icon = currentMessage.icon;

  return (
    <div className="w-full bg-gradient-primary shadow-lg animate-[fadeIn_0.2s_ease-out]">
      <div className="container mx-auto">
        <div className="flex items-center justify-between gap-2 sm:gap-4 py-1.5 sm:py-3">
          <div className="flex items-center gap-2 sm:gap-4 flex-1 justify-center">
            <div className="flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-white/20 backdrop-blur-sm flex-shrink-0">
              <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-black" />
            </div>

            <p
              key={currentMessageIndex}
              className={`text-xs sm:text-sm font-medium text-black transition-all duration-300 ${
                animating ? 'opacity-0 -translate-y-5' : 'opacity-100 translate-y-0'
              }`}
            >
              <Link to={currentMessage.link} className="hover:underline">
                <span className="sm:hidden">{currentMessage.mobileText}</span>
                <span className="hidden sm:inline">{currentMessage.text}</span>
                <span className="ml-1 underline font-semibold">{currentMessage.mobileCta} →</span>
              </Link>
            </p>
          </div>

          <button
            onClick={onDismiss}
            className="flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors text-black"
            aria-label="Dismiss banner"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
